import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { PlusIcon } from 'lucide-react';
import { faqItems } from '../data/methodology';
import { sourceIndex } from '../utils/sourceIndex';
import { PageHeader } from '../components/PageHeader';
import { RichTextView } from '../components/RichTextView';
import { DataBadge } from '../components/DataBadge';
import { COMPILED_LABEL } from '../data/sources';
export function Methodology() {
  const [open, setOpen] = useState<string[]>([faqItems[0].id]);
  const toggle = (id: string) => setOpen(o => o.includes(id) ? o.filter(x => x !== id) : [...o, id]);
  const external = sourceIndex.filter(s => !s.url.startsWith('/'));
  const internal = sourceIndex.filter(s => s.url.startsWith('/'));
  return <>
      <PageHeader title="Methodology" lede="How the count is made, where it is weak, and every source it rests on. The numbered markers across the site point to the index at the bottom of this page." data-id="element-710" data-mp="3taih7mo2d6ox">
        <DataBadge mode="snapshot" source="Methodology" asOf={COMPILED_LABEL} data-id="element-711" data-mp="3572b1r1wiqu7" />
      </PageHeader>

      <div className="mx-auto grid max-w-[1400px] gap-12 px-5 pt-10 md:px-8 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]" data-id="element-712" data-mp="306el78pesg1k">
        <section aria-label="Questions" data-id="element-713" data-mp="2w5uno40xbaew">
          <ul className="border-t border-line" data-id="element-714" data-mp="2r6rj0kouv67g">
            {faqItems.map(f => {
            const isOpen = open.includes(f.id);
            return <li key={f.id} id={f.id} className="scroll-mt-32 border-b border-line" data-id="element-715" data-mp="3nvz9860f63hp">
                  <h2 data-id="element-716" data-mp="3jvoy6exrsmvz">
                    <button type="button" aria-expanded={isOpen} aria-controls={`${f.id}-panel`} onClick={() => toggle(f.id)} className="flex w-full items-center justify-between gap-4 py-5 text-left text-[17px] text-ink" data-id="element-717" data-mp="3jxjchaejdgr3">
                      {f.question}
                      <PlusIcon className={`h-4 w-4 shrink-0 text-muted transition-transform duration-200 ease-out ${isOpen ? 'rotate-45' : ''}`} aria-hidden="true" data-id="element-718" data-mp="6mkbm16gayo2" />
                    </button>
                  </h2>
                  <AnimatePresence initial={false} data-id="element-719" data-mp="gsk6khtuoks2">
                    {isOpen && <motion.div id={`${f.id}-panel`} initial={{
                  height: 0,
                  opacity: 0
                }} animate={{
                  height: 'auto',
                  opacity: 1
                }} exit={{
                  height: 0,
                  opacity: 0
                }} transition={{
                  duration: 0.22,
                  ease: [0.23, 1, 0.32, 1]
                }} className="overflow-hidden" data-id="element-720" data-mp="gqr99t54ncwq">
                        <div className="space-y-3 pb-6 text-[14.5px] leading-relaxed text-ink/85" data-id="element-721" data-mp="1v1ilhozyblo">
                          {f.paragraphs.map((p, i) => <p key={i} data-id="element-722" data-mp="1s8ikayzz3uzm" data-mpi={i}>
                              <RichTextView text={p} data-id="element-723" data-mp="1w92i84sdf2mt" />
                            </p>)}
                        </div>
                      </motion.div>}
                  </AnimatePresence>
                </li>;
          })}
          </ul>
        </section>

        <section id="sources" aria-labelledby="sources-title" className="scroll-mt-32" data-id="element-724" data-mp="3qhpqm2wmla0z">
          <div className="flex items-baseline justify-between" data-id="element-725" data-mp="3lqpugzvbyvzj">
            <h2 id="sources-title" className="font-serif text-2xl text-ink" data-id="element-726" data-mp="3lod7g8aptvfm">
              Data sources index
            </h2>
            <span className="font-mono text-[12px] text-muted" data-id="element-727" data-mp="3vgj806f19l6f">{sourceIndex.length} entries</span>
          </div>
          <p className="mt-1 text-[13px] text-muted" data-id="element-728" data-mp="db67gil969sx">Generated from every source referenced across the site. Numbers match the markers.</p>
          <ol className="mt-5 divide-y divide-line border-y border-line" data-id="element-729" data-mp="8c30utpmjviu">
            {external.map(s => <li key={s.n} id={`src-${s.n}`} className="grid grid-cols-[2.5rem_minmax(0,1fr)] gap-2 py-2.5" data-id="element-730" data-mp="2f29hb5nbjkcf">
                <span className="font-mono text-[12px] text-machine" data-id="element-731" data-mp="2a5ar2y6rzxji">[{s.n}]</span>
                <div className="min-w-0" data-id="element-732" data-mp="2chbl9x4bgeac">
                  <a href={s.url} target="_blank" rel="noopener noreferrer" className="text-[13.5px] text-ink hover:text-machine" data-id="element-733" data-mp="2giwvtbtdoghd">
                    {s.name}
                  </a>
                  <p className="truncate font-mono text-[11px] text-faint" data-id="element-734" data-mp="2qhcjt5x3z9mn">
                    {s.publisher} · {s.kind}
                  </p>
                </div>
              </li>)}
          </ol>
          <h3 className="mt-8 text-[14px] font-medium text-ink" data-id="element-735" data-mp="1xsfee3dzx7bb">Our own estimates</h3>
          <ol className="mt-3 divide-y divide-line border-y border-line" data-id="element-736" data-mp="22rs0glx1macu">
            {internal.map(s => <li key={s.n} className="grid grid-cols-[2.5rem_minmax(0,1fr)] gap-2 py-2.5" data-id="element-737" data-mp="27i8eo88ou009">
                <span className="font-mono text-[12px] text-machine" data-id="element-738" data-mp="1a349c9pm4qrd">[{s.n}]</span>
                <span className="text-[13.5px] text-ink/85" data-id="element-739" data-mp="1a19zbqfvqj22">{s.name}</span>
              </li>)}
          </ol>
        </section>
      </div>
    </>;
}